/**
 * Unified observability bootstrap for the React app — Sentry + PostHog.
 *
 * One source of truth: GET /api/public-sentry-config (legacy name; returns
 * both Sentry + PostHog config). If neither key is returned, both SDKs
 * stay silent — toggle the whole stack from the backend `.env`.
 */
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import api from "@/lib/api";

let _sentryInitialized = false;
let _posthogInitialized = false;

const _SENSITIVE_HEADER_FRAGMENTS = ["cookie", "authorization", "x-api-key", "x-csrf"];

function _scrub(event) {
  try {
    const req = event.request || {};
    if (req.headers && typeof req.headers === "object") {
      for (const k of Object.keys(req.headers)) {
        if (_SENSITIVE_HEADER_FRAGMENTS.some((f) => k.toLowerCase().includes(f))) {
          req.headers[k] = "[Filtered]";
        }
      }
    }
    if (req.cookies) req.cookies = "[Filtered]";
    event.request = req;
  } catch {
    // never let scrubber throw
  }
  return event;
}

function _scrubBreadcrumb(breadcrumb) {
  try {
    if (breadcrumb.category === "xhr" || breadcrumb.category === "fetch") {
      const data = breadcrumb.data || {};
      if (typeof data.url === "string") {
        try {
          const u = new URL(data.url, window.location.origin);
          data.url = u.pathname;
        } catch {
          // ignore parse errors
        }
      }
      breadcrumb.data = data;
    }
  } catch {
    return null;
  }
  return breadcrumb;
}

/** Boot Sentry + PostHog by asking the backend for runtime config.
 *  Safe to call before login. Never throws. */
export async function initSentry() {
  if (_sentryInitialized && _posthogInitialized) return;
  try {
    const { data } = await api.get("/public-sentry-config");
    if (!data) return;

    // ---------- Sentry ----------
    const sentryCfg = data.sentry || data; // tolerate old shape
    if (!_sentryInitialized && sentryCfg?.dsn) {
      Sentry.init({
        dsn: sentryCfg.dsn,
        environment: sentryCfg.environment || "preview",
        release: sentryCfg.release || "afyanex-frontend@dev",
        sendDefaultPii: false,
        sampleRate: 1.0,
        tracesSampleRate: 0.0,
        beforeSend: _scrub,
        beforeBreadcrumb: _scrubBreadcrumb,
      });
      _sentryInitialized = true;
    }

    // ---------- PostHog ----------
    const phCfg = data.posthog;
    if (!_posthogInitialized && phCfg?.api_key) {
      posthog.init(phCfg.api_key, {
        api_host: phCfg.host || "https://us.i.posthog.com",
        // Explicit-only capture — no autocapture, no automatic pageviews,
        // no session recording. PHI-safe defaults.
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        enable_recording_console_log: false,
        // We don't use PostHog feature flags here — disabling the /decide
        // round-trip makes init succeed even when ad-blockers or strict
        // CSP environments block the us-assets endpoint.
        advanced_disable_decide: true,
        // Don't persist a person across logouts (we explicitly reset())
        persistence: "localStorage+cookie",
        loaded: (ph) => {
          if (phCfg.release) ph.register({ release: phCfg.release });
          // Expose for diagnostics + debugging in dev tools console
          if (typeof window !== "undefined") window.posthog = ph;
        },
      });
      _posthogInitialized = true;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[observability] init skipped:", err?.message || err);
  }
}

/** Push the authenticated user's tenancy onto both Sentry scope + PostHog identity. */
export function tagSentryUser(user) {
  // Sentry scope tags
  if (_sentryInitialized) {
    try {
      if (!user) {
        Sentry.setUser(null);
        Sentry.setTag("agency_id", "anonymous");
        Sentry.setTag("user_role", "anonymous");
      } else {
        Sentry.setUser({ id: user.id, role: user.role });
        Sentry.setTag("agency_id", user.agency_id || "unknown");
        Sentry.setTag("user_role", user.role || "unknown");
        Sentry.setTag("superadmin", user.is_superadmin ? "true" : "false");
      }
    } catch {
      // ignore
    }
  }
  // PostHog identity + group
  if (_posthogInitialized) {
    try {
      if (!user) {
        posthog.reset();
      } else {
        posthog.identify(user.id, { user_role: user.role || "unknown" });
        posthog.group("agency", user.agency_id || "unknown");
        posthog.register({
          agency_id: user.agency_id || "unknown",
          user_role: user.role || "unknown",
          is_superadmin: !!user.is_superadmin,
        });
      }
    } catch {
      // ignore
    }
  }
}

/** Capture an explicit product-analytics event. Auto-tagged with super
 *  properties (agency_id, user_role, release) set by initSentry+tagSentryUser. */
export function track(event, properties = {}) {
  if (!_posthogInitialized) return;
  try {
    posthog.capture(event, { source: "frontend", ...properties });
  } catch {
    // never let analytics raise into UI code
  }
}

export { Sentry, posthog };
